/* eslint-disable react-hooks/set-state-in-effect -- The hook resets derived local share state when source inputs change. */
/**
 * useViralShare Hook
 *
 * Custom hook for viral share button logic (state, handlers, analytics).
 * Extracted from ViralShareButton.tsx (Line Count Refactor 2025-11-21)
 *
 * Phase 0.5d Integration: Hybrid Pattern (2025-12-09)
 * - Shows client-side templates immediately (zero latency)
 * - Upgrades to workflow-generated content when available
 * - Multi-model consensus: A/B test gate for validation
 */
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Platform } from "react-native";
import * as Sharing from "expo-sharing";
import type {
	DramaAnalysisResponse,
	MessageContext,
} from "@dramadetector/shared";
import { useTranslation } from "@dramadetector/shared";
import {
	useGeneratedContent,
	type ShareCopyVariant,
} from "@dramadetector/api-client";
import { authenticatedFetch } from "../../../packages/api-client/src/utils/authenticated-fetch";
import { useAuth } from "../contexts/AuthContext";
import { useShareAnalysis } from "../features/share/useShareAnalysis";
import { useCreateSharedLink } from "./useCreateSharedLink";
import { captureEvent } from "../lib/analytics";
import { generateEpisodeTitle } from "../lib/generateEpisodeTitle";
import {
	assertResponseOk,
	getAuthHeaders,
	getEdgeFunctionUrl,
} from "../lib/api-url";
import type {
	ShareCardRendererRef,
	ShareCardData,
} from "../components/share/ShareCardRenderer";
import {
	generateShareTemplates,
	generateShareTemplatesForSubject,
	getVisibleShareSubjects,
	truncateText,
	type ShareSubject,
	type ShareTemplate,
} from "../lib/share/shareTemplates";
import { formatIssueType } from "../features/analysis/utils/formatting";
import type { ShareFormat } from "@dramadetector/shared/og";

const slugifyFilenameSegment = (value?: string | null) => {
	const normalized = (value ?? "share")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

	return normalized || "share";
};

const getWebNavigator = () => {
	if (Platform.OS !== "web") {
		return undefined;
	}

	return globalThis.navigator as
		| (typeof globalThis.navigator & {
				canShare?: (data?: { files?: unknown[] }) => boolean;
				share?: (data?: { files?: unknown[]; title?: string }) => Promise<void>;
			})
		| undefined;
};

const getWebUserAgent = () => {
	const navigatorRef = getWebNavigator();
	return navigatorRef?.userAgent || "unknown";
};

const getWebFileConstructor = () => {
	if (Platform.OS !== "web") {
		return undefined;
	}

	return globalThis.File as
		| (new (
				fileBits: Array<Blob | string>,
				fileName: string,
				options?: { type?: string },
			) => {
				name: string;
				type: string;
			})
		| undefined;
};

const buildGeneratedImageFilename = (
	subjectTitle: string | undefined,
	format: ShareFormat,
) => `drama-detector-${slugifyFilenameSegment(subjectTitle)}-${format}.png`;

interface UseViralShareProps {
	analysis: DramaAnalysisResponse;
	context?: MessageContext;
	shareMode?: "text" | "image";
	sessionCount?: number;
	/** Session ID from analyze-drama response (for workflow content lookup) */
	requestId?: string;
	/** Analysis ID — when provided, auto-generates a shareable link */
	analysisId?: string;
	/** Journey OG image URL for rewrite-journey share cards (#1884) */
	journeyOgImageUrl?: string;
	/** Selected format for multi-format share cards */
	selectedFormat?: ShareFormat;
}

export function useViralShare({
	analysis,
	context,
	shareMode = "image",
	sessionCount = 1,
	requestId,
	analysisId,
	journeyOgImageUrl,
	selectedFormat = "reel",
}: UseViralShareProps) {
	const { t } = useTranslation("features");
	const { t: tShare } = useTranslation("share");
	const { user, accessToken } = useAuth();
	const [showModal, setShowModal] = useState(false);
	const [cardData, setCardData] = useState<ShareCardData | null>(null);
	const [onRendered, setOnRendered] = useState<(() => void) | undefined>(
		undefined,
	);
	const { shareAnalysis, isSharing } = useShareAnalysis();
	const {
		shortUrl,
		isLoading: isCreatingLink,
		publish: publishResultLink,
		revoke: revokeResultLink,
	} = useCreateSharedLink(analysisId);

	const [copiedLink, setCopiedLink] = useState(false);
	const [selectedShareSubjectId, setSelectedShareSubjectId] = useState<
		string | null
	>(null);
	const [generatedAssetState, setGeneratedAssetState] = useState<
		"idle" | "generating" | "ready" | "failed"
	>("idle");
	const [generatedImageUri, setGeneratedImageUri] = useState<string | null>(
		null,
	);
	const cardRendererRef = useRef<ShareCardRendererRef>(null);
	const webGeneratedImageBlobRef = useRef<Blob | null>(null);
	const resetGeneratedAsset = useCallback(() => {
		webGeneratedImageBlobRef.current = null;
		setGeneratedImageUri((current) => {
			if (
				current &&
				Platform.OS === "web" &&
				typeof URL !== "undefined" &&
				current.startsWith("blob:")
			) {
				URL.revokeObjectURL(current);
			}
			return null;
		});
		setGeneratedAssetState("idle");
		setCardData(null);
		setOnRendered(undefined);
	}, []);

	// Feature flag for A/B testing workflow content (Phase 0.5d)
	// Web uses window.posthog, native uses posthog-react-native
	const [workflowShareCopyEnabled, setWorkflowShareCopyEnabled] =
		useState(false);

	// eslint-disable-next-line local-rules/no-race-conditions -- independent PostHog bootstrap effect; does not coordinate with share-subject synchronization
	useEffect(() => {
		// Check feature flag - web uses window.posthog, native deferred to future
		if (Platform.OS === "web" && typeof window !== "undefined") {
			const checkFlag = () => {
				try {
					if (window.posthog?.isFeatureEnabled) {
						const enabled = window.posthog.isFeatureEnabled(
							"workflow_share_copy_enabled",
						);
						setWorkflowShareCopyEnabled(enabled === true);
					}
				} catch {
					// PostHog not ready, keep default (false)
				}
			};

			// Check immediately and after short delay (PostHog may not be ready)
			checkFlag();
			const timeout = setTimeout(checkFlag, 1000);
			return () => clearTimeout(timeout);
		}
		// Native: disabled for now (posthog-react-native hook requires provider)
	}, []);

	// Fetch workflow-generated content (if available and enabled)
	const { data: workflowContent, contentSource } = useGeneratedContent({
		userId: user?.id,
		sessionId: requestId || "",
		enabled: workflowShareCopyEnabled && !!requestId && !!user,
	});

	const shareSubjects = useMemo(
		() => getVisibleShareSubjects(analysis),
		[analysis],
	);
	const normalizedDramaScore = analysis.result?.score ?? analysis.score ?? 0;
	const normalizedDramaCategory =
		analysis.result?.category ?? analysis.category ?? "safe";

	// eslint-disable-next-line local-rules/no-race-conditions -- derived selection sync effect; independent from feature-flag bootstrap
	useEffect(() => {
		if (
			selectedShareSubjectId &&
			shareSubjects.some((subject) => subject.id === selectedShareSubjectId)
		) {
			return;
		}

		setSelectedShareSubjectId(shareSubjects[0]?.id ?? null);
	}, [shareSubjects, selectedShareSubjectId]);

	const selectedShareSubject = useMemo<ShareSubject | null>(
		() =>
			shareSubjects.find((subject) => subject.id === selectedShareSubjectId) ??
			shareSubjects[0] ??
			null,
		[shareSubjects, selectedShareSubjectId],
	);

	useEffect(() => {
		resetGeneratedAsset();
	}, [resetGeneratedAsset, selectedShareSubjectId, selectedFormat]);

	// Generate share templates based on the selected share subject (client-side fallback)
	const clientTemplates = selectedShareSubject
		? generateShareTemplatesForSubject(
				selectedShareSubject,
				analysis,
				context,
				t as (key: string, params?: Record<string, unknown>) => string,
			)
		: generateShareTemplates(
				analysis,
				context,
				t as (key: string, params?: Record<string, unknown>) => string,
			);

	// Hybrid pattern: Use workflow content when available, fall back to client-side
	const templates = useMemo<ShareTemplate[]>(() => {
		// If workflow content has sharecopy variants, convert them to templates
		if (contentSource === "workflow" && workflowContent?.sharecopy?.variants) {
			const workflowTemplates = convertWorkflowToTemplates(
				workflowContent.sharecopy.variants,
				t as (key: string, params?: Record<string, unknown>) => string,
			);

			// Track content source for analytics
			captureEvent("ShareCopy_ContentSource", {
				source: "workflow",
				variant_count: workflowTemplates.length,
				model_used: workflowContent.modelUsed,
				tier: workflowContent.tier,
			});

			return workflowTemplates.length > 0 ? workflowTemplates : clientTemplates;
		}

		// Fall back to client-side templates
		if (contentSource === "template") {
			captureEvent("ShareCopy_ContentSource", {
				source: "template",
				variant_count: clientTemplates.length,
				reason: requestId ? "workflow_not_ready" : "no_request_id",
			});
		}

		return clientTemplates;
	}, [contentSource, workflowContent, clientTemplates, t, requestId]);

	/**
	 * Open share modal and track event
	 */
	const handleOpenShareModal = () => {
		resetGeneratedAsset();
		// Track share initiated (modal opened)
		captureEvent("Share_Initiated", {
			drama_score: normalizedDramaScore,
			context: context || "none",
			template_count: templates.length,
			platform: Platform.OS,
		});

		// Analysis_ShareModalOpened retired (#797 -- Share_Initiated is canonical)

		setShowModal(true);
	};

	/**
	 * Close share modal
	 */
	const handleCloseShareModal = () => {
		setShowModal(false);
		resetGeneratedAsset();
	};

	const handleSelectShareSubject = (subjectId: string) => {
		setSelectedShareSubjectId(subjectId);
		resetGeneratedAsset();
	};

	/**
	 * Handle template selection and share
	 */
	const handleShare = async (template: ShareTemplate) => {
		// Track template selection
		captureEvent("Share_TemplateSelected", {
			template_type: template.type,
			character_voice: template.voice,
			drama_score: normalizedDramaScore,
			context: context || "none",
			share_mode: shareMode,
			share_format: selectedFormat,
			platform: Platform.OS,
		});

		captureEvent("Analysis_ShareTemplateSelected", {
			template_type: template.type,
			character_voice: template.voice,
			score: normalizedDramaScore,
			share_mode: shareMode,
			platform: Platform.OS as "ios" | "android" | "web",
			user_agent:
				Platform.OS === "web" ? getWebUserAgent() : `${Platform.OS}-native`,
		});

		// Track workflow template selection (if from workflow)
		if (contentSource === "workflow" && workflowContent) {
			captureEvent("WorkflowShare_TemplateSelected", {
				template_type: template.type,
				platform: (template as { platform?: string }).platform || "twitter",
				voice: template.voice,
				drama_score: normalizedDramaScore,
				model_used: workflowContent.modelUsed || "unknown",
				tier: workflowContent.tier || "unknown",
			});
		}

		if (shareMode === "image") {
			if (generatedAssetState === "ready" && generatedImageUri) {
				setShowModal(false);
				await shareGeneratedImage(template, generatedImageUri);
				return;
			}

			await handleImageShare(template);
			return;
		}

		setShowModal(false);
		await handleTextShare(template);
	};

	/**
	 * Handle TEXT-BASED sharing (traditional text-based sharing)
	 */
	const handleTextShare = async (template: ShareTemplate) => {
		// Format share text with hashtags and shareable URL (if available)
		// When a journey OG image URL is available, append it so link previews show the before/after card
		const shareUrl = journeyOgImageUrl || shortUrl;
		const fullShareText = `${template.shareText}\n\n${shareUrl ? `${shareUrl}\n\n` : ""}${template.hashtags.join(" ")}`;

		// Use existing share hook (handles clipboard fallback, haptics, etc.)
		const result = await shareAnalysis(analysis, {
			overrideText: fullShareText,
		});

		// Track share completion
		if (result === "shared" || result === "copied") {
			captureEvent("Share_Completed", {
				template_type: template.type,
				character_voice: template.voice,
				share_method: result,
				share_mode: "text",
				drama_score: normalizedDramaScore,
				context: context || "none",
				platform: Platform.OS,
			});

			captureEvent("Analysis_ShareCompleted", {
				template_type: template.type,
				share_method: result,
				share_mode: "text",
				score: normalizedDramaScore,
				platform: Platform.OS as "ios" | "android" | "web",
				user_agent:
					Platform.OS === "web" ? getWebUserAgent() : `${Platform.OS}-native`,
			});
		} else if (result === "error") {
			captureEvent("Share_Failed", {
				template_type: template.type,
				share_mode: "text",
				drama_score: normalizedDramaScore,
				platform: Platform.OS,
			});
		}
	};

	const generateImageAssetFromWorker = async (template: ShareTemplate) => {
		if (!accessToken) {
			throw new Error("Not authenticated");
		}

		const activeSubject = selectedShareSubject ?? shareSubjects[0] ?? null;
		if (!activeSubject) {
			throw new Error("No share subject selected");
		}

		const response = await authenticatedFetch(
			getEdgeFunctionUrl("generate-share-image"),
			{
				method: "POST",
				headers: {
					...getAuthHeaders(accessToken),
				},
				body: JSON.stringify({
				analysisId: analysisId ?? "share-export-preview",
				format: selectedFormat,
				subject: activeSubject,
				template: {
					id: template.id,
					type: template.type,
					voice: template.voice,
					title: template.title,
					description: template.description,
					shareText: template.shareText,
					hashtags: template.hashtags,
				},
				card: {
					dramaScore: normalizedDramaScore,
					category: normalizedDramaCategory,
					episodeTitle: generateEpisodeTitle(sessionCount, context, analysis),
				},
				meta: {
					source: "export-composer",
					appSurface: "web",
				},
			}),
			},
		);

		await assertResponseOk(response, "Failed to generate share image");
		const imageBlob = await response.blob();
		webGeneratedImageBlobRef.current = imageBlob;
		return URL.createObjectURL(imageBlob);
	};

	/**
	 * Handle IMAGE-BASED sharing (new card generation)
	 */
	const handleImageShare = async (template: ShareTemplate) => {
		try {
			setGeneratedAssetState("generating");

			let imageUri: string | null | undefined;

			if (Platform.OS === "web") {
				imageUri = await generateImageAssetFromWorker(template);
			} else {
				// Step 1: Map template to card data
				const cardDataForTemplate = mapTemplateToCardData(template);

				// Step 2: Wait for off-screen render using onRendered callback
				await new Promise<void>((resolve) => {
					setOnRendered(() => resolve);
					setCardData(cardDataForTemplate);
				});

				// Step 3: Capture image using ViewShot
				imageUri = await cardRendererRef.current?.captureImage();
			}

			if (!imageUri) {
				throw new Error("Image capture returned null");
			}

			setGeneratedImageUri(imageUri);
			setGeneratedAssetState("ready");
			setCardData(null);
			setOnRendered(undefined);
		} catch (error) {
			console.error("[useViralShare] Image generation failed:", error);
			setGeneratedAssetState("failed");
			setGeneratedImageUri((current) => {
				if (
					current &&
					Platform.OS === "web" &&
					typeof URL !== "undefined" &&
					current.startsWith("blob:")
				) {
					URL.revokeObjectURL(current);
				}
				return null;
			});
			setCardData(null);
			setOnRendered(undefined);

			captureEvent("Share_Failed", {
				template_type: template.type,
				share_mode: "image",
				share_format: selectedFormat,
				fallback_to_text: false,
				error_message: error instanceof Error ? error.message : "Unknown error",
				drama_score: normalizedDramaScore,
				platform: Platform.OS,
			});
		}
	};

	const trackImageCompletion = (
		shareMethod: "shared" | "download",
		template?: ShareTemplate,
	) => {
		captureEvent("Share_Completed", {
			template_type: template?.type,
			character_voice: template?.voice,
			share_method: shareMethod,
			share_mode: "image",
			share_format: selectedFormat,
			drama_score: normalizedDramaScore,
			context: context || "none",
			platform: Platform.OS,
		});

		captureEvent("Analysis_ShareCompleted", {
			template_type: template?.type,
			share_method: shareMethod,
			share_mode: "image",
			share_format: selectedFormat,
			score: normalizedDramaScore,
			platform: Platform.OS as "ios" | "android" | "web",
			user_agent:
				Platform.OS === "web" ? getWebUserAgent() : `${Platform.OS}-native`,
		});
	};

	const downloadGeneratedImage = async (
		imageUri: string,
		template?: ShareTemplate,
	) => {
		if (Platform.OS === "web" && typeof document !== "undefined") {
			const link = document.createElement("a");
			link.href = imageUri;
			link.download = buildGeneratedImageFilename(
				selectedShareSubject?.title,
				selectedFormat,
			);
			link.rel = "noopener";
			if (link.style) {
				link.style.display = "none";
			}
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			trackImageCompletion("download", template);
			return;
		}

		const canShare = await Sharing.isAvailableAsync();
		if (!canShare) {
			throw new Error("Sharing not available on this device");
		}

		await Sharing.shareAsync(imageUri, {
			mimeType: "image/jpeg",
			dialogTitle: tShare("share.dialog_title"),
		});
		trackImageCompletion("download", template);
	};

	const shareGeneratedImageOnWeb = async (
		template: ShareTemplate,
		imageUri: string,
	) => {
		const navigatorRef = getWebNavigator();
		const FileConstructor = getWebFileConstructor();
		const imageBlob = webGeneratedImageBlobRef.current;

		if (!navigatorRef?.share || !FileConstructor || !imageBlob) {
			await downloadGeneratedImage(imageUri, template);
			return;
		}

		const filename = buildGeneratedImageFilename(
			selectedShareSubject?.title,
			selectedFormat,
		);
		const imageFile = new FileConstructor([imageBlob], filename, {
			type: imageBlob.type || "image/png",
		});

		if (navigatorRef.canShare && !navigatorRef.canShare({ files: [imageFile] })) {
			await downloadGeneratedImage(imageUri, template);
			return;
		}

		await navigatorRef.share({
			files: [imageFile],
			title: tShare("share.dialog_title"),
		});
		trackImageCompletion("shared", template);
	};

	const shareGeneratedImage = async (
		template: ShareTemplate,
		imageUri: string,
	) => {
		if (Platform.OS === "web") {
			await shareGeneratedImageOnWeb(template, imageUri);
			return;
		}

		const canShare = await Sharing.isAvailableAsync();
		if (!canShare) {
			throw new Error("Sharing not available on this device");
		}

		await Sharing.shareAsync(imageUri, {
			mimeType: "image/jpeg",
			dialogTitle: tShare("share.dialog_title"),
		});
		trackImageCompletion("shared", template);
	};

	const handleDownloadImage = async (template?: ShareTemplate) => {
		if (!generatedImageUri) {
			return;
		}

		await downloadGeneratedImage(generatedImageUri, template);
	};

	/**
	 * Map ShareTemplate to ShareCardData (determines card type and content)
	 */
	const mapTemplateToCardData = (template: ShareTemplate): ShareCardData => {
		const episodeTitle = generateEpisodeTitle(sessionCount, context, analysis);
		const activeSubject = selectedShareSubject ?? shareSubjects[0] ?? null;
		const patternTag = truncateText(
			activeSubject?.title || "Communication Pattern",
			30,
		);
		const insightText = truncateText(
			activeSubject?.body || template.shareText,
			200,
		);

		// Map template types to card types
		switch (template.type) {
			case "episode_framing":
			case "reality_tv_commentary":
				// Narrator voice → Narrator card
				return {
					type: "narrator",
					episodeTitle,
					dramaScore: normalizedDramaScore,
					context,
				};

			case "pattern_recognition":
			case "advice_wisdom":
				// Expert voice → Expert card
				return {
					type: "expert",
					insightText,
					patternTag,
					dramaScore: normalizedDramaScore,
				};

			default:
				// Fallback: Combined card (best of both worlds)
				return {
					type: "combined",
					episodeTitle,
					dramaScore: normalizedDramaScore,
					insightText,
					patternTag,
				};
		}
	};

	/**
	 * Publish the conservative result projection only after the caller has
	 * displayed its disclosure and confirmation control.
	 */
	const handlePublishResultLink = async () => {
		await publishResultLink(["score", "summary"]);
	};

	/** Owner-initiated revocation; the hook clears the displayed URL on success. */
	const handleRevokeResultLink = async () => {
		await revokeResultLink();
	};

	/**
	 * Copy an already-published shareable link to clipboard.
	 */
	const handleCopyLink = async () => {
		if (!shortUrl) return;
		try {
			if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(shortUrl);
			}
			setCopiedLink(true);
			setTimeout(() => setCopiedLink(false), 2000);
			captureEvent("Share_LinkCopied", {
				url_type: "public-result",
			});
		} catch {
			console.warn("[useViralShare] Clipboard not available");
		}
	};

	return {
		// State
		showModal,
		cardData,
		onRendered,
		isSharing,
		templates,
		shareSubjects,
		selectedShareSubjectId,
		generatedAssetState,
		cardRendererRef,
		getCardDataForTemplate: mapTemplateToCardData,
		/** Content source: 'workflow' (AI-generated) or 'template' (client-side) */
		contentSource,
		/** SHORT URL: /s/{short_id} - Canonical shareable link */
		shortUrl,
		/** Whether the link is still being created in the background */
		isCreatingLink,
		/** Whether the link was just copied (resets after 2s) */
		copiedLink,

		// Handlers
		handleOpenShareModal,
		handleCloseShareModal,
		handleShare,
		handlePublishResultLink,
		handleRevokeResultLink,
		handleCopyLink,
		handleSelectShareSubject,
		handleDownloadImage,
		resetGeneratedAsset,
	};
}

/**
 * Convert workflow-generated ShareCopyVariant to ShareTemplate format
 *
 * Maps platform-specific AI-generated copy to the template UI structure.
 */
function convertWorkflowToTemplates(
	variants: ShareCopyVariant[],
	t: (key: string, params?: Record<string, unknown>) => string,
): ShareTemplate[] {
	const platformToVoice: Record<string, "narrator" | "expert"> = {
		twitter: "narrator",
		instagram: "narrator",
		tiktok: "narrator",
		linkedin: "expert",
		sms: "expert",
	};

	const platformToType: Record<string, ShareTemplate["type"]> = {
		twitter: "episode_framing",
		instagram: "reality_tv_commentary",
		tiktok: "reality_tv_commentary",
		linkedin: "pattern_recognition",
		sms: "advice_wisdom",
	};

	return variants.map((variant, index) => ({
		id: `workflow_${variant.platform}_${index}`,
		type: platformToType[variant.platform] || "episode_framing",
		voice: platformToVoice[variant.platform] || "narrator",
		title: t(`share.platforms.${variant.platform}.title`, {
			defaultValue: variant.platform,
		}),
		description: variant.hook || t("share.workflow.description"),
		shareText: variant.copy,
		hashtags: [
			"#DramaDetector",
			variant.cta ? `#${variant.cta.replace(/\s+/g, "")}` : "#Drama",
		],
	}));
}
